import type { IconType } from "react-icons";
import {
  FaImage,
  FaVideo,
  FaTable,
  FaFileWord,
  FaFilePdf,
  FaFile,
  FaFileCsv,
  FaFileExcel,
} from "react-icons/fa6";
import { FiFile, FiImage } from "react-icons/fi";
import { VscJson } from "react-icons/vsc";
import { EXTENSION_KIND_MAP } from "./fileTypes";

const DEFAULT_BROWSE_ICON_SIZE = 18;

type IconConfig = {
  Icon: IconType;
  className?: string;
  size?: number;
};

const FILE_ICON_VARIANTS: Record<string, Record<string, IconConfig>> = {
  browse: {
    csv: { Icon: FaFileCsv, className: "text-green-500" },
    excel: { Icon: FaFileExcel, className: "text-green-600" },
    pdf: { Icon: FaFilePdf, className: "text-red-500" },
    json: { Icon: VscJson, className: "text-yellow-500" },
    image: { Icon: FiImage, className: "text-cyan-400" },
    video: { Icon: FaVideo, className: "text-gray-400" },
    doc: { Icon: FaFileWord, className: "text-gray-400" },
    text: { Icon: FiFile, className: "text-gray-400" },
    file: { Icon: FiFile, className: "text-gray-400" },
  },
  card: {
    csv: { Icon: FaTable },
    excel: { Icon: FaTable },
    pdf: { Icon: FaFilePdf },
    json: { Icon: FaFile },
    image: { Icon: FaImage },
    video: { Icon: FaVideo },
    doc: { Icon: FaFileWord },
    text: { Icon: FaFile },
    file: { Icon: FaFile },
  },
};

export const getFileIconData = (
  path: unknown,
  options: { variant?: string } = {}
): IconConfig => {
  const { variant = "browse" } = options;
  const normalizedPath = typeof path === "string" ? path : "";
  const extension = normalizedPath.split(".").pop()?.toLowerCase() || "";
  const kind = EXTENSION_KIND_MAP[extension] || "file";
  const variantMap = FILE_ICON_VARIANTS[variant] || FILE_ICON_VARIANTS.browse;
  const config = variantMap[kind] || variantMap.file;

  return {
    ...config,
    size:
      config.size ||
      (variant === "browse" ? DEFAULT_BROWSE_ICON_SIZE : undefined),
  };
};

export const iconForFileType = (
  path: unknown,
  options: { variant?: string } = {}
) => {
  const { variant = "card" } = options;
  const { Icon, className: defaultClassName, size: defaultSize } =
    getFileIconData(path, { variant });

  return (props: { className?: string; size?: number } = {}) => {
    const mergedClassName = [props.className, defaultClassName]
      .filter(Boolean)
      .join(" ");
    const resolvedSize = props.size ?? defaultSize;

    return <Icon {...props} className={mergedClassName} size={resolvedSize} />;
  };
};
